'use client';

import { useTranslations } from 'next-intl';
import { DraftingCompass } from 'lucide-react';
import { cn } from '@/shared/lib/cn';

/**
 * Permanent "this is a reconstruction, not a photograph" marker.
 *
 * A building with `is_reconstruction: true` has never been photographed — every
 * image on the screen was CONSTRUCTED from the user's drawings and brief. The
 * product rule is absolute: a reconstruction must never be presented as the
 * user's house. Wherever a result is shown (render, album, and above all the
 * estimate, where a guess turns into money) this marker rides along, and it is
 * never dismissible: the moment it can be closed, the next person to open the
 * file sees an invented building presented as a survey.
 *
 * `compact` is the on-image pill; the default is the in-flow chip used under
 * document/album rows.
 */
export function ReconstructionMarker({
  compact = false,
  className,
}: {
  compact?: boolean;
  className?: string;
}) {
  const t = useTranslations('project');

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full text-chip',
        compact
          ? 'bg-black/45 px-2 py-0.5 text-white backdrop-blur-sm'
          : 'bg-muted px-2.5 py-1 text-muted-foreground',
        className
      )}
    >
      <DraftingCompass className="h-3.5 w-3.5 shrink-0" />
      {t('reconstructionMarker')}
    </span>
  );
}

/**
 * Frame content for a CONSTRUCTED view that has not been rendered yet.
 *
 * Without this the design page is simply blank for a building with no photo:
 * every image slot is keyed off the angle's file URL, and a constructed view
 * has none. The placeholder says what the empty frame is waiting for instead of
 * reading as a broken screen.
 */
export function ConstructedViewPlaceholder() {
  const t = useTranslations('project');

  return (
    <div className="flex aspect-[16/9] flex-col items-center justify-center gap-2 bg-image-backdrop p-6 text-center">
      <DraftingCompass className="h-10 w-10 text-muted-foreground/60" />
      <p className="text-list-item-title text-foreground">
        {t('constructedViewTitle')}
      </p>
      <p className="max-w-md text-form-help text-muted-foreground">
        {t('constructedViewDescription')}
      </p>
    </div>
  );
}
